import { memo, useMemo } from 'react';
import {
  ChatStoreContextProvider,
  useCreateChatStore,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore';
import { ReviewLevel, TaskType, PipelineV3Name } from '@scale/shared-types';
import { ChatInteraction } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatInteraction';
import { useCommonChatTaskContextConfigs } from 'react-scale/components/scalers/tasks/Chat/hooks/configs';
import transformLookupToAssignment from '@scale/shared/assignmentJson/transformLookupToAssignment';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import useSupplementedMultiTaskResponseAssignment from 'react-scale/hooks/useSupplementedMultiTaskResponseAssignment';
import _ from 'lodash';
import {
  ChatPreviewApp as ChatTaskV2,
  ChatPreviewHLEApp,
  ChatCustomerViewApp as ChatV2CustomerApp,
} from '@scale/chat-lite-tool';
import { isValidMongoId } from 'react-scale/corp/views/CorpProjectGroupManager/utils/helpers';
import ContributorActionsWrapper from './ContributorActionsWrapper';
import { UnresolvedPipelineV3NodeId } from '@scale/task-builder-shared';
import { useGetProjectPipelineV3Config } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { TaskParamsVersion } from './types';
import logger from '@scale/frontend-lib/logging';
import { useAsync } from 'react-use-v13';
import { useStore } from 'zustand';
import { loadResponseWithMaybeUrl } from 'react-scale/components/scalers/tasks/Chat/utils';
import { ScaleLogoLoading } from '@scale/scaleui';
import { EnhancedErrorBoundary } from './EnhancedErrorBoundary';

// Chat task and Chat lite teams to review
// This will take either a task or a taskAttempt from ReadOnlyGenericTaskWithResponse
export const ChatTaskWrapper = memo(
  ({
    task,
    asCustomer = false,
    extras = {},
    overrideReviewLevel,
    taskParamsVersion,
  }: {
    task: any;
    asCustomer?: boolean;
    extras?: any;
    overrideReviewLevel?: number;
    taskParamsVersion?: TaskParamsVersion;
  }) => {
    const { taskAssignment, taskKey } = useMemo(() => {
      return {
        taskAssignment: transformLookupToAssignment(task),
        taskKey: `${task._id || task.id}-${taskParamsVersion || 'most-recent'}`,
      };
    }, [task, taskParamsVersion]);
    // This hook is re-render around 65 times, which is most likely one of the the reason of the page freezing
    // This can be optimized but I don't think there is a clear owner on the "assigment" as concept/entity
    // To follow up with the chat teams to figure who should own this optimization
    // For context the hook calls many FF's that are not cached but I don't know how to tell which ones are required (some for sure are not as the GIS ones which is AVCV)
    const supplementedAssignment = useSupplementedMultiTaskResponseAssignment(taskAssignment);

    const { assignment, chatTaskFeatureConfig } = useMemo(() => {
      const finalAssignment = supplementedAssignment ?? taskAssignment;
      return {
        assignment: finalAssignment,
        chatTaskFeatureConfig: finalAssignment?.projectConfig?.chatTaskFeatureConfig,
      };
    }, [supplementedAssignment, taskAssignment]);

    return (task.type ?? task.taskType) === TaskType.Chat ? (
      <ChatV1Preview
        assignment={assignment}
        task={task}
        asCustomer={asCustomer}
        chatTaskFeatureConfig={chatTaskFeatureConfig}
        key={taskKey}
      />
    ) : (
      <EnhancedErrorBoundary resetKeys={[taskParamsVersion]}>
        <ChatV2Preview
          key={taskKey}
          assignment={assignment}
          task={task}
          asCustomer={asCustomer}
          extras={extras}
          overrideReviewLevel={overrideReviewLevel}
          taskParamsVersion={taskParamsVersion}
        />
      </EnhancedErrorBoundary>
    );
  },
);

const ChatV2Preview = memo(
  ({
    task,
    assignment,
    asCustomer = false,
    extras = {},
    overrideReviewLevel,
    taskParamsVersion,
  }: {
    task: any;
    assignment: any;
    asCustomer?: boolean;
    extras?: {
      shouldShowContributorActions?: boolean;
      transactionHistoryId?: UnresolvedPipelineV3NodeId;
    };
    overrideReviewLevel?: number;
    taskParamsVersion?: TaskParamsVersion;
  }) => {
    const projectId = useMemo(
      () => [task.project?._id, task.project, task.projectId].find(isValidMongoId),
      [task],
    );

    const { data: pipelineConfig } = useGetProjectPipelineV3Config(
      task?.project?._id ?? task?.project,
    );
    const { pipelineName } = pipelineConfig ?? {};

    const { value: resolvedResponse, loading: isLoadingResponse } = useAsync(async () => {
      return await loadResponseWithMaybeUrl(task.response, projectId);
    }, [task.response, projectId]);

    const useInTaskCollaboration = useMemo(() => {
      const hasReplicas = !!task.response && 'replicas' in task.response;
      return hasReplicas && pipelineName === PipelineV3Name.InTaskCollaborationPipeline;
    }, [task, pipelineName]);

    const taskParams = useMemo(() => {
      try {
        // Default fallback for both branches
        let selectedParams = task.params ?? task.task.params;

        // Only override with taskParamsFromV3Node if we're in time-of-tasking mode and it's valid
        if (
          taskParamsVersion === 'time-of-tasking' &&
          task.taskParamsFromV3Node &&
          typeof task.taskParamsFromV3Node === 'object'
        ) {
          selectedParams = task.taskParamsFromV3Node;
        }

        if (!selectedParams || typeof selectedParams !== 'object') {
          return {};
        }

        return selectedParams;
      } catch (error) {
        logger.error('Error getting task params', error);
        return {};
      }
    }, [task, taskParamsVersion]);

    if (isLoadingResponse) {
      return (
        <div className="flex items-center justify-center h-full w-full pt-16">
          <ScaleLogoLoading size="lg" />
        </div>
      );
    }

    const response = resolvedResponse ?? task.response;

    return (
      <div className="my-4 relative min-h-[60vh]">
        {extras.shouldShowContributorActions ? (
          <ContributorActionsWrapper
            transactionHistoryId={extras.transactionHistoryId}
            assignment={assignment}
            reviewLevel={(assignment?.reviewLevel ?? ReviewLevel.Deliverable) as number}
            response={response}
            params={taskParams}
            className="absolute inset-0"
          />
        ) : !asCustomer ? (
          useInTaskCollaboration ? (
            <ChatPreviewHLEApp
              assignment={assignment}
              reviewLevel={(assignment?.reviewLevel ?? ReviewLevel.Deliverable) as number}
              response={response}
              params={taskParams}
              className="absolute inset-0"
            />
          ) : (
            <ChatTaskV2
              assignment={assignment}
              reviewLevel={
                overrideReviewLevel ?? assignment?.reviewLevel ?? ReviewLevel.Deliverable
              }
              response={response}
              params={taskParams}
            />
          )
        ) : (
          <ChatV2CustomerApp
            assignment={assignment}
            response={response}
            params={taskParams}
            className="absolute inset-0"
          />
        )}
      </div>
    );
  },
);

const ChatV1Preview = memo(
  ({
    task,
    assignment,
    chatTaskFeatureConfig,
    asCustomer = false,
  }: {
    task: any;
    assignment: any;
    chatTaskFeatureConfig: any;
    asCustomer?: boolean;
  }) => {
    const config = useMemo(
      () => task.taskParamsFromV3Node ?? task.params ?? task.task.params,
      [task],
    );

    const chatStore = useCreateChatStore(
      {
        config,
        reviewLevel: assignment?.reviewLevel ?? ReviewLevel.Deliverable,
        hasActiveStepResponseLinterError: false,
      },
      undefined,
      'ChatV1Preview - ChatStore',
    );

    const setResponsesAndCompletedSteps = useStore(
      chatStore.current,
      state => state.setResponsesAndCompletedSteps,
    );

    const commonChatTaskContextConfigs = useCommonChatTaskContextConfigs(
      config,
      assignment?.reviewLevel,
      chatTaskFeatureConfig,
      config.templateVariables,
    );

    const projectId = useMemo(
      () => [task.project._id, task.project, task.projectId].find(isValidMongoId),
      [task],
    );

    const { loading: isLoadingResponse } = useAsync(async () => {
      const response = await loadResponseWithMaybeUrl(task.response, projectId);
      setResponsesAndCompletedSteps(response.responses);
    });

    if (task && !projectId) {
      return (
        <div>
          There was a problem loading the project for this task. Please contact support as this is
          shouldn't happen. Tag @genai-ops-app-oncall in #genai-help for assistance.
        </div>
      );
    }

    if (isLoadingResponse) {
      return (
        <div className="flex items-center justify-center h-full w-full pt-16">
          <ScaleLogoLoading size="lg" />
        </div>
      );
    }

    return (
      <div className="my-4">
        <ChatStoreContextProvider store={chatStore}>
          <ChatTaskContext.Provider
            value={{
              assignmentId: assignment?.assignmentId,
              taskAttemptIdWithFeedback: task.id,
              projectId,
              ...commonChatTaskContextConfigs,
              chatBenchmarkConfig: _.get(assignment, 'metadata.chatBenchmarkConfig'),
              isInitialAttempt: false,
              canEditComments: false,
              isPipelineV2: true,
              disableLinter: true,
              enableUnifiedFeedback: !asCustomer,
              contextSource: 'ChatBulkAudit',
            }}
          >
            <ChatInteraction key={task._id} readOnly enableLayoutOverride />
          </ChatTaskContext.Provider>
        </ChatStoreContextProvider>
      </div>
    );
  },
);
